import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/*
    🔥 METADATOS DE LA PÁGINA

    Título, descripción, canonical y tarjetas para redes sociales.

    ⚠️ SIN react-helmet-async

    La versión vieja dependía de esa librería. Acá se manipula
    document.head directamente en un efecto: cero dependencias, anda en
    React 18 y 19 por igual, y evita el proveedor extra en el árbol.

    Para renderizado en servidor sí hace falta una solución con
    proveedor, pero este template es una aplicación de una sola página.

    ⚠️ CADA ETIQUETA SE MARCA COMO PROPIA

    Todas llevan data-ls-seo. Al desmontar se borran SOLO esas: las
    etiquetas que escribiste a mano en index.html quedan intactas. Sin
    esa marca, cambiar de página borraría el favicon y el viewport.

    ⚠️ EL TÍTULO NO SE RESTAURA AL DESMONTAR

    A propósito. Entre que una vista se va y la siguiente se monta hay
    un instante; si restauráramos, el título parpadearía al nombre de
    la empresa en cada navegación.
*/

const MARK = 'data-ls-seo';

const onSetMeta = (attr, key, content) => {

    if (!content) return;

    const SELECTOR = `meta[${attr}="${key}"]`;

    let el = document.head.querySelector(SELECTOR);

    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute(MARK, '');
        document.head.appendChild(el);
    }

    el.setAttribute('content', content);
};

const onSetLink = (rel, href) => {

    if (!href) return;

    let el = document.head.querySelector(`link[rel="${rel}"][${MARK}]`);

    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute(MARK, '');
        document.head.appendChild(el);
    }

    el.setAttribute('href', href);
};

export const AppSeo = ({
    title = null,
    description = null,
    image = null,
    url = null,
    type = 'website',            // website | article | profile
    noIndex = false,
    siteName = null,
    baseUrl = null,
    locale = 'es_AR',
    publishedAt = null,
    author = null,
    tags = [],
    isEnabled = true,
    jsonLd = null,               // objeto de datos estructurados
}) => {

    const location = useLocation();

    useEffect(() => {

        if (!isEnabled) return;

        const SITE = siteName ?? document.title.split('|').pop()?.trim() ?? '';

        const FULL_TITLE = title ? (SITE ? `${title} | ${SITE}` : title) : SITE;

        const ORIGIN = baseUrl ?? window.location.origin;

        const CANONICAL = `${ORIGIN}${url ?? location.pathname}`;

        // -------------------- 🎯 TÍTULO
        if (FULL_TITLE) document.title = FULL_TITLE;

        // -------------------- 🎯 BÁSICOS
        onSetMeta('name', 'description', description);

        /*
            ⚠️ noindex se ESCRIBE, no se omite.

            Omitirlo deja la etiqueta de la página anterior en el head y
            una pantalla privada terminaría indexable por herencia.
        */
        onSetMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');

        onSetLink('canonical', CANONICAL);

        // -------------------- 🎯 REDES
        onSetMeta('property', 'og:type', type);
        onSetMeta('property', 'og:title', FULL_TITLE);
        onSetMeta('property', 'og:description', description);
        onSetMeta('property', 'og:url', CANONICAL);
        onSetMeta('property', 'og:locale', locale);
        onSetMeta('property', 'og:site_name', SITE);
        onSetMeta('property', 'og:image', image);

        /*
            🔥 summary_large_image solo si hay imagen. Con la tarjeta
               grande y sin imagen, el enlace se ve roto en Twitter.
        */
        onSetMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
        onSetMeta('name', 'twitter:title', FULL_TITLE);
        onSetMeta('name', 'twitter:description', description);
        onSetMeta('name', 'twitter:image', image);

        // -------------------- 🎯 ARTÍCULO
        if (type === 'article') {
            onSetMeta('property', 'article:published_time', publishedAt);
            onSetMeta('property', 'article:author', author);
        }

        // -------------------- 🎯 DATOS ESTRUCTURADOS
        let script = null;

        if (jsonLd) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute(MARK, '');
            script.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }

        // -------------------- 🎯 LIMPIEZA
        return () => {
            /*
                ⚠️ Solo se borran las etiquetas de artículo y el JSON-LD.

                Las de descripción y og: las pisa la vista siguiente. Si
                las borráramos acá, entre dos vistas quedaría un instante
                sin metadatos, y los previsualizadores de enlaces que
                leen rápido se llevan la página vacía.
            */
            document.head
                .querySelectorAll(`meta[property^="article:"][${MARK}]`)
                .forEach((el) => el.remove());

            script?.remove();
        };

    }, [
        title, description, image, url, type, noIndex, siteName, baseUrl,
        locale, publishedAt, author, isEnabled, jsonLd, location.pathname,
    ]);

    // 🔥 No pinta nada: todo su trabajo sucede en el <head>
    return null;
};

/*
    🔥 ATAJOS DE DATOS ESTRUCTURADOS

    Los dos que aparecen en casi todo proyecto. Google los usa para
    mostrar migas y datos de la organización en los resultados.
*/
export const onBuildOrganizationJsonLd = ({ name, url, logo, sameAs = [] }) => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo,
    sameAs,
});

export const onBuildBreadcrumbJsonLd = (items = [], baseUrl = '') => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.href ? `${baseUrl}${item.href}` : undefined,
    })),
});
